import type { ITrackProps } from '@zillow/universal-analytics';
import { event } from '@zillow/universal-analytics';

import type { getHomeRecsQuery } from '../__generated__/graphql';
import { CAROUSEL_PROPERTY_LIMIT } from '../constants/carousel';
import type { CollectionTrackingItem, GenericCollection } from '../types/genericTypes';

/**
 * Shapes listing button click events.
 * @param {String} label GA listing button label
 * @return {ITrackProps} GA event payload
 */
export const formatListingButtonGAEvent = (label: string): ITrackProps => ({
    category: 'link',
    action: 'click',
    label,
});

/**
 * Returns the tracking tag for the collections response
 * @param {getHomeRecsQuery} data Data returned from the zg-graph home recs query
 * @returns {string} Tracking tag for the collections response
 */
const getTrackingTag = (data: getHomeRecsQuery): string =>
    data.collections?.trackingTags?.[0]?.value ?? '';

/**
 * Given collections response data and an index for a specific collection, this returns an array of
 * collection items that a user could see with relevant data for clickstream event tracking
 * @param {getHomeRecsQuery} data Data returned from the zg-graph home recs query
 * @param {number} index Index of the current collection
 * @returns {CollectionTrackingItem[]} Array of CollectionTrackingItems with zpid and rec_rank
 */
const getCollectionItems = (data: getHomeRecsQuery, index: number): CollectionTrackingItem[] => {
    const currentCollection = data.collections?.collections?.[index];
    if (!currentCollection) {
        return [];
    }

    const { propertyDetails } = currentCollection;
    if (!propertyDetails?.length) {
        return [];
    }

    const zpids: number[] = propertyDetails
        .map((property) => property?.zpid)
        .filter((zpid): zpid is number => typeof zpid === 'number')
        .slice(0, CAROUSEL_PROPERTY_LIMIT);

    return zpids.map((curr, i) => ({
        zpid: `${curr}`,
        // eslint-disable-next-line zillow/@typescript-eslint/naming-convention
        rec_rank: `${i + 1}`,
    }));
};

/**
 * Given collections response data, this returns an array containing required
 * clickstream data for each collection in the repsonse
 * @param {getHomeRecsQuery} data Data returned from the zg-graph home recs query
 * @returns {GenericCollection[]} Array of GenericCollections with required data
 */
export const getGenericCollection = (data: getHomeRecsQuery): GenericCollection[] => {
    const allCollections = data.collections?.collections;
    if (!allCollections) {
        return [];
    }

    const trackingTag = getTrackingTag(data);
    return allCollections.map((curr, i) => ({
        /* eslint-disable zillow/@typescript-eslint/naming-convention */
        // eslint-disable-next-line no-underscore-dangle
        collection_type_txt: curr?.__typename ?? '',
        collection_title_txt: curr?.name ?? '',
        collection_data: {
            module_rank_nb: `${i + 1}`,
            tracing_id: trackingTag,
        },
        collection_items: getCollectionItems(data, i),
        /* eslint-enable zillow/@typescript-eslint/naming-convention */
    }));
};

/**
 * Tracks all the collections data on the page
 * @param {getHomeRecsQuery} data Data returned from the zg-graph home recs query
 * @return {void}
 */
export const trackCollectionsData = (data: getHomeRecsQuery) => {
    const eventStartTime = new Date().toISOString();

    const clickstreamProperties = {
        /* eslint-disable zillow/@typescript-eslint/naming-convention */
        clickstream_trigger: {
            trigger_type_nm: 'impression',
            trigger_location_nm: 'home',
            trigger_source_nm: 'home',
            trigger_object_nm: 'no_trigger_object',
        },
        envelope: {
            event_template_version_id: '1',
            event_template_id: '249',
            event_type_version_id: '1',
            event_type_id: '4808',
            event_client_start_dtm: eventStartTime,
        },
        semantic: {
            semantic_event_nm: 'view_content',
        },
        generic_collection: {
            items: getGenericCollection(data),
        },
        /* eslint-enable zillow/@typescript-eslint/naming-convention */
    };

    event(clickstreamProperties);
};
